import { MapPin, Phone, Mail, Clock, MessageCircle, Star } from "lucide-react";
import { CLINIC, waLink } from "../lib/clinic";
import SafeImage from "../components/site/SafeImage";
import { CLINIC_PHOTOS } from "../lib/clinicPhotos";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";
import SymptomCheckerTeaser from "../components/site/SymptomCheckerTeaser";

const Contact = () => {
  usePageMeta({
    title: "Contact & Directions | Dr. Shantanu Gupta, Varanasi",
    path: "/contact",
    description: "Visit Shantanu Netralaya Eye Hospital at 40, Deen Dayal Colony, Kabir Nagar Road, Near Durgakund, Varanasi 221005. Call +91 83186 65154 or WhatsApp for appointment.",
  });
  usePageJsonLd(graph([breadcrumb([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]));
  return (
    <div data-testid="contact-page">
      <section className="pt-20 pb-10 container-page">
        <div className="overline mb-4">Visit Us</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
          Find us in the heart of Varanasi.
        </h1>
      </section>

      <section className="container-page" data-testid="contact-hero-photo">
        <SafeImage
          src={CLINIC_PHOTOS.exteriorDay}
          alt="Shantanu Netralaya Eye Hospital, Varanasi — front view"
          label="Shantanu Netralaya"
          className="w-full rounded-2xl border border-border"
          style={{ aspectRatio: "16/9", maxHeight: 520 }}
        />
      </section>
      <section className="py-8 container-page grid lg:grid-cols-2 gap-12 items-start">
        <div className="space-y-6">
          <div className="border border-border rounded-2xl p-6">
            <div className="flex gap-4"><MapPin size={22} className="text-[hsl(var(--accent))]" /><div>
              <div className="overline">Address</div>
              <div className="mt-1 text-[15px]">{CLINIC.address}</div>
              <a href={CLINIC.mapLink} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm text-[hsl(var(--primary))] underline" data-testid="contact-directions">Get directions →</a>
            </div></div>
          </div>
          <div className="border border-border rounded-2xl p-6">
            <div className="flex gap-4"><Phone size={22} className="text-[hsl(var(--accent))]" /><div>
              <div className="overline">Phone / WhatsApp</div>
              <div className="mt-1"><a href={`tel:${CLINIC.phoneRaw}`} className="text-[15px] hover:text-[hsl(var(--primary))]">Clinic: {CLINIC.phone}</a></div>
              <div><a href={waLink()} target="_blank" rel="nofollow noopener noreferrer" className="text-[15px] hover:text-[hsl(var(--primary))]">WhatsApp: {CLINIC.whatsapp}</a></div>
            </div></div>
          </div>
          <div className="border border-border rounded-2xl p-6">
            <div className="flex gap-4"><Mail size={22} className="text-[hsl(var(--accent))]" /><div>
              <div className="overline">Email</div>
              <a href={`mailto:${CLINIC.email}`} className="mt-1 block text-[15px] break-all hover:text-[hsl(var(--primary))]">{CLINIC.email}</a>
            </div></div>
          </div>
          <div className="border border-border rounded-2xl p-6">
            <div className="flex gap-4"><Clock size={22} className="text-[hsl(var(--accent))]" /><div>
              <div className="overline">Timings</div>
              <div className="mt-1 space-y-1">
                {CLINIC.timings.map((t) => <div key={t.day} className="text-[15px]"><span className="font-medium">{t.day}: </span>{t.hours}</div>)}
              </div>
            </div></div>
          </div>
          <a href={waLink()} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary" data-testid="contact-book-btn">
            <MessageCircle size={16} /> Book on WhatsApp
          </a>
        </div>
        <div className="rounded-2xl overflow-hidden border border-border h-[600px] shadow-sm">
          <iframe
            title="Shantanu Netralaya Location"
            src={CLINIC.mapEmbed}
            width="100%"
            height="100%"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 0 }}
            data-testid="contact-map"
          />
        </div>
      </section>

      {/* Google review + Q&A block — reinforces the GMB entity link and
          pushes patients to leave reviews (highest local-pack ranking lever). */}
      <section className="py-16 border-t border-border container-page" data-testid="google-cta-block">
        <div className="grid md:grid-cols-2 gap-6">
          <a
            href={CLINIC.googleWriteReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-write-google-review"
            className="group relative rounded-2xl border border-border bg-card p-6 sm:p-8 hover:border-[hsl(var(--accent))] transition-colors flex items-start gap-5"
          >
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--accent))]/12 flex items-center justify-center shrink-0">
              <Star size={26} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
            </div>
            <div className="min-w-0">
              <div className="overline">Google · One-tap review</div>
              <div className="mt-2 font-serif text-2xl leading-tight">Share your experience on Google.</div>
              <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed">
                Your honest review helps other Varanasi families find good eye care — and takes under 30 seconds. Opens Google's review form directly.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 text-[13px] text-[hsl(var(--accent))] group-hover:translate-x-1 transition-transform">
                Leave a review →
              </div>
            </div>
          </a>
          <a
            href={CLINIC.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-read-google-reviews"
            className="group relative rounded-2xl border border-border bg-card p-6 sm:p-8 hover:border-[hsl(var(--primary))] transition-colors flex items-start gap-5"
          >
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--primary))]/12 flex items-center justify-center shrink-0">
              <MessageCircle size={26} strokeWidth={1.5} className="text-[hsl(var(--primary))]" />
            </div>
            <div className="min-w-0">
              <div className="overline">Google · Business Profile</div>
              <div className="mt-2 font-serif text-2xl leading-tight">Read all reviews &amp; ask a question.</div>
              <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed">
                See every review, browse photos, or post a question in Google's Q&amp;A — Dr. Shantanu answers directly.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 text-[13px] text-[hsl(var(--primary))] group-hover:translate-x-1 transition-transform">
                Open on Google Maps →
              </div>
            </div>
          </a>
        </div>
      </section>
      <section className="py-20 border-t border-border container-page" data-testid="contact-clinic-photos">
        <div className="overline mb-4">Inside the clinic</div>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight max-w-2xl">
          What to expect when you walk in.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <figure className="rounded-xl overflow-hidden border border-border group">
            <SafeImage src={CLINIC_PHOTOS.reception} alt="Reception at Shantanu Netralaya" label="Reception" className="aspect-[4/5] w-full" />
            <figcaption className="p-4 text-sm text-muted-foreground">Reception & TPA / Insurance help desk on the ground floor.</figcaption>
          </figure>
          <figure className="rounded-xl overflow-hidden border border-border group">
            <SafeImage src={CLINIC_PHOTOS.waitingArea} alt="Waiting area" label="Waiting Area" className="aspect-[4/5] w-full" />
            <figcaption className="p-4 text-sm text-muted-foreground">Bright, air-conditioned waiting area with informational displays about premium IOLs.</figcaption>
          </figure>
          <figure className="rounded-xl overflow-hidden border border-border group">
            <SafeImage src={CLINIC_PHOTOS.recoveryRoom} alt="Day-care recovery ward" label="Recovery Ward" className="aspect-[4/5] w-full" />
            <figcaption className="p-4 text-sm text-muted-foreground">Clean, calm day-care ward for post-operative rest & observation.</figcaption>
          </figure>
        </div>
      </section>

      <section className="container-page pb-16">
        <SymptomCheckerTeaser context="contact" />
      </section>
    </div>
  );
};

export default Contact;
